import { Notification } from "@/utils/notificationUtils";
import { queryOptions } from "@tanstack/react-query";
import dayjs from "dayjs";
import t from "../../app/i18n";
import { HttpStatus } from "../../common/api/http";
import { AUTH_QUERY_KEYS } from "../../common/api/queries";
import { queryClient } from "../../common/api/queryClient";
import type { UUID } from "../../typings/global";
import { clientSearchActions } from "../client/store";
import { authApi } from "./api";
import { authActions, getAuthSnapshot } from "./store";
import type { AuthenticatedLoginResponse, Session } from "./types";
import { findUserAccount, getRoleUserAccounts, isActiveUserAccount } from "./utils";

const toSession = ({ user, totpDevices }: AuthenticatedLoginResponse): Session => ({
  user,
  totpDevices: totpDevices ?? [],
});

/**
 * Everything but the session belongs to the account - AppLayout remounts for a new one and refetches behind its
 * skeleton.
 */
export const removeAccountData = () => {
  queryClient.removeQueries({ predicate: ({ queryKey }) => queryKey[0] !== AUTH_QUERY_KEYS.session()[0] });
  clientSearchActions().sessionEnded();
};

/**
 * The token refresh doubles as the current user request - it returns the user with their accounts and permissions
 * as the server has them now.
 */
export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: AUTH_QUERY_KEYS.session(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const refreshedToken = getAuthSnapshot().session?.token;
      const { data } = await authApi.refreshToken();
      authActions().tokenRefreshed(refreshedToken, data);

      return toSession(data);
    },
    staleTime: Infinity,
    gcTime: Infinity,
    // At least a minute apart, so a failing refresh of a token already due is not repeated right away, and at most
    // three days, since setInterval overflows past about 24 days and would fire right away.
    refetchInterval: () => {
      const validUntil = getAuthSnapshot().session?.validUntil;

      return validUntil
        ? Math.min(Math.max(dayjs(validUntil).subtract(10, "minute").diff(dayjs()), 60_000), 3 * 24 * 60 * 60_000)
        : false;
    },
    refetchIntervalInBackground: true,
    // An unauthorized refresh has already ended the session in the response interceptor.
    retry: (failureCount, error) => error.status !== HttpStatus.UNAUTHORIZED && failureCount < 2,
  });

// One instance posts and listens, so a tab never receives its own switch.
export const accountSwitchChannel = new BroadcastChannel("cliip:account-switch");

const getSessionUser = () => queryClient.getQueryData(sessionQueryOptions().queryKey)?.user;

export const startSession = (response: AuthenticatedLoginResponse) => {
  authActions().authenticated(response);

  // A login refetches everything, however fresh the cache looks.
  removeAccountData();
  queryClient.setQueryData(sessionQueryOptions().queryKey, toSession(response));
};

// useSessionSync clears the query cache once the session is gone.
export const endSession = () => authActions().sessionEnded();

export const switchAccount = (accountId: UUID) => {
  const { selectedAccount } = getAuthSnapshot();

  if (
    accountId === selectedAccount.accountId ||
    !findUserAccount(getSessionUser(), { ...selectedAccount, accountId })
  ) {
    return;
  }

  authActions().accountSwitched(accountId);
  accountSwitchChannel.postMessage(accountId);

  Notification.getNotification().success({
    title: t("helpers.accountSwitch"),
    description: t("helpers.accountSwitchDescription"),
  });
};

/**
 * For a change to the user's own data - their name, an account or the agent behind it.
 */
export const refreshSession = () => queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.session() });

/**
 * For a change to a user - their name or their accounts. Only a change of the current user refreshes the session.
 */
export const userChanged = (userId: UUID) => {
  if (getSessionUser()?.id === userId) {
    refreshSession();
  }
};

/**
 * Requests of a removed account are unauthorized, so the session moves to another active account before it
 * refreshes - a user left without one is logged out by the refresh.
 */
export const userAccountRemoved = (userId: UUID, accountId: UUID) => {
  const { selectedAccount } = getAuthSnapshot();

  if (accountId === selectedAccount.accountId) {
    const nextAccount = getRoleUserAccounts(getSessionUser(), selectedAccount.role).find(
      (account) => account.id !== accountId && isActiveUserAccount(account),
    );

    if (nextAccount) {
      switchAccount(nextAccount.id);
    }
  }

  userChanged(userId);
};
